"use client";

import { useEffect, useState } from "react";
import { useUser } from "@clerk/nextjs";

export function useAdminStatus() {
  const { isLoaded, isSignedIn, user } = useUser();
  const activeUserId = isLoaded && isSignedIn ? (user?.id ?? null) : null;
  const [adminStatus, setAdminStatus] = useState<{
    userId: string;
    isAdmin: boolean;
  } | null>(null);

  useEffect(() => {
    if (!activeUserId) return;

    const userId = activeUserId;
    const controller = new AbortController();

    async function loadAdminStatus() {
      try {
        const response = await fetch("/api/admin/status", {
          cache: "no-store",
          signal: controller.signal,
        });
        if (!response.ok) {
          setAdminStatus({ userId, isAdmin: false });
          return;
        }
        const data = (await response.json()) as { isAdmin?: boolean };
        setAdminStatus({ userId, isAdmin: data.isAdmin === true });
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") return;
        setAdminStatus({ userId, isAdmin: false });
      }
    }

    loadAdminStatus();
    return () => controller.abort();
  }, [activeUserId]);

  return {
    isLoaded,
    isAdminLoaded: isLoaded && (!activeUserId || adminStatus?.userId === activeUserId),
    isSignedIn: isSignedIn === true,
    userId: user?.id ?? null,
    isAdmin:
      activeUserId !== null &&
      adminStatus?.userId === activeUserId &&
      adminStatus.isAdmin,
  };
}
